import React, { useCallback, useEffect, useState, useMemo } from 'react';
// Removed Firebase imports - now using backend APIs
import scanReportsService from '../services/scanReports.js';
import groupReviewsService from '../services/groupReviews.js';
import { auth } from '../firebase';

const normalizeGroupIdentifier = (value) =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/^(link:|name:)/, '')
    .replace(/^https?:\/\//, '')
    .replace(/^t\.me\/s\//, 't.me/')
    .replace(/\/$/, '');

const normalizeReviewStatus = (review) => {
  if (typeof review?.isFake === 'boolean') {
    return review.isFake ? 'fraudulent' : 'genuine';
  }

  const raw = (review?.status || review?.reviewStatus || review?.result || '')
    .toString()
    .trim()
    .toLowerCase();

  if (['fake', 'fraud', 'fraudulent', 'scam', 'malicious'].includes(raw)) {
    return 'fraudulent';
  }

  if (['suspicious', 'suspect', 'monitor', 'monitoring', 'watchlist'].includes(raw)) {
    return 'suspicious';
  }

  if (['genuine', 'real', 'safe', 'legit', 'legitimate', 'authentic'].includes(raw)) {
    return 'genuine';
  }

  return 'unknown';
};

const getReviewKeyword = (review) =>
  (review?.keywordQuery || review?.keyword || review?.searchKeyword || '')
    .toString()
    .trim();

const normalizeLlmDecision = (value) => {
  const raw = (value || '').toString().trim().toLowerCase();
  if (!raw) return 'unknown';
  if (raw.includes('likely_fraud') || raw === 'fraud' || raw === 'fraudulent') return 'fraud';
  if (raw.includes('likely_legit') || raw === 'genuine' || raw === 'legit') return 'genuine';
  if (raw.includes('uncertain')) return 'uncertain';
  return 'unknown';
};

const llmDecisionDisplay = (value) => {
  const decision = normalizeLlmDecision(value);
  if (decision === 'fraud') return 'Fraud';
  if (decision === 'genuine') return 'Genuine';
  if (decision === 'uncertain') return 'Uncertain';
  return 'Unknown';
};

const REVIEW_LIST_PAGE_SIZE = 50;
const SUMMARY_FETCH_LIMIT = 500;

const INITIAL_SUMMARY = {
  generatedAt: null,
  totalScans: 0,
  totalScannedGroups: 0,
  totalHits: 0,
  reviewedGroups: 0,
  fraudulentGroups: 0,
  suspiciousGroups: 0,
  genuineGroups: 0,
  unmarkedGroups: 0,
  reviewRecords: 0,
  reviewCoverage: 0,
  latestScanAt: null,
  latestReviewAt: null,
};

const parseDateValue = (value) => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const collectScanGroupKeys = (report) => {
  const keys = new Set();

  if (Array.isArray(report?.scanGroupKeys)) {
    report.scanGroupKeys.forEach((key) => {
      const normalized = normalizeGroupIdentifier(key);
      if (normalized) keys.add(normalized);
    });
  }

  if (!keys.size && Array.isArray(report?.groupsDetails)) {
    report.groupsDetails.forEach((group) => {
      const normalized = normalizeGroupIdentifier(group?.groupKey || group?.groupLink || group?.groupName || '');
      if (normalized) keys.add(normalized);
    });
  }

  if (!keys.size && report?.groupLink) {
    const normalized = normalizeGroupIdentifier(report.groupLink);
    if (normalized) keys.add(normalized);
  }

  if (!keys.size && report?.groupName) {
    const normalized = normalizeGroupIdentifier(report.groupName);
    if (normalized) keys.add(normalized);
  }

  return keys;
};

const getReportKeywordQuery = (report) => {
  const direct = (report?.keywordQuery || report?.searchKeyword || report?.searchPhrase || '')
    .toString()
    .trim();
  if (direct) return direct;

  if (Array.isArray(report?.keywords) && report.keywords.length > 0) {
    return report.keywords.join(', ');
  }

  return '';
};

const buildDatabaseSummary = (scanReports = [], reviews = []) => {
  const uniqueScannedGroups = new Set();
  const latestReviewsByGroup = new Map();
  let totalHits = 0;
  let latestScanAt = null;
  let latestReviewAt = null;

  scanReports.forEach((report) => {
    totalHits += Number(report?.totalHits || report?.total_hits || 0);

    const reportTime = parseDateValue(report?.createdAt || report?.updatedAt || report?.timestamp || null);
    if (reportTime && (!latestScanAt || reportTime > latestScanAt)) {
      latestScanAt = reportTime;
    }

    collectScanGroupKeys(report).forEach((key) => uniqueScannedGroups.add(key));
  });

  reviews.forEach((review) => {
    const reviewTime = parseDateValue(review?.timestamp || review?.createdAt || review?.updatedAt || null);
    if (reviewTime && (!latestReviewAt || reviewTime > latestReviewAt)) {
      latestReviewAt = reviewTime;
    }

    const reviewKey = normalizeGroupIdentifier(review?.groupLink) || normalizeGroupIdentifier(review?.groupName) || `id:${review?.id || ''}`;
    if (!reviewKey) return;

    const existing = latestReviewsByGroup.get(reviewKey);
    if (!existing || (reviewTime && reviewTime > existing.timestamp)) {
      latestReviewsByGroup.set(reviewKey, {
        timestamp: reviewTime,
        status: normalizeReviewStatus(review),
      });
    }
  });

  let fraudulentGroups = 0;
  let suspiciousGroups = 0;
  let genuineGroups = 0;

  latestReviewsByGroup.forEach((latestReview) => {
    if (latestReview.status === 'fraudulent') fraudulentGroups += 1;
    if (latestReview.status === 'suspicious') suspiciousGroups += 1;
    if (latestReview.status === 'genuine') genuineGroups += 1;
  });

  const reviewedGroups = latestReviewsByGroup.size;

  const unmarkedGroups = Math.max(uniqueScannedGroups.size - reviewedGroups, 0);
  const reviewCoverage = uniqueScannedGroups.size
    ? Math.round((reviewedGroups / uniqueScannedGroups.size) * 100)
    : 0;

  return {
    generatedAt: new Date().toISOString(),
    totalScans: scanReports.length,
    totalScannedGroups: uniqueScannedGroups.size,
    totalHits,
    reviewedGroups,
    fraudulentGroups,
    suspiciousGroups,
    genuineGroups,
    unmarkedGroups,
    reviewRecords: reviews.length,
    reviewCoverage,
    latestScanAt: latestScanAt ? latestScanAt.toISOString() : null,
    latestReviewAt: latestReviewAt ? latestReviewAt.toISOString() : null,
  };
};

const csvEscape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const fetchAllGroupReviews = async (baseFilters = {}) => {
  const allReviews = [];
  let offset = 0;

  while (true) {
    const response = await groupReviewsService.getReviews({
      ...baseFilters,
      limit: SUMMARY_FETCH_LIMIT,
      offset,
    });
    const batch = response?.reviews || [];
    allReviews.push(...batch);

    if (!response?.hasMore || batch.length === 0) {
      return {
        ...response,
        reviews: allReviews,
        hasMore: false,
      };
    }

    offset += SUMMARY_FETCH_LIMIT;
  }
};

export default function Settings() {
  const user = auth.currentUser;
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(INITIAL_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const isDateFilterActive = Boolean(startDate || endDate);

  // Load scan reports and group reviews from backend API
  const loadDatabaseOverview = useCallback(async (pageToLoad = 1) => {
    try {
      console.log('[FrudDB] Loading database overview from API...');
      const offset = (pageToLoad - 1) * REVIEW_LIST_PAGE_SIZE;
      const reviewFilters = {
        limit: REVIEW_LIST_PAGE_SIZE,
        offset,
      };
      if (startDate) reviewFilters.startDate = new Date(`${startDate}T00:00:00`).toISOString();
      if (endDate) reviewFilters.endDate = new Date(`${endDate}T23:59:59`).toISOString();

      const summaryDateFilters = {
        ...(reviewFilters.startDate ? { startDate: reviewFilters.startDate } : {}),
        ...(reviewFilters.endDate ? { endDate: reviewFilters.endDate } : {}),
      };
      const isDateFiltered = Boolean(reviewFilters.startDate || reviewFilters.endDate);
      const reviewListPromise = isDateFiltered
        ? fetchAllGroupReviews(summaryDateFilters)
        : groupReviewsService.getReviews(reviewFilters);
      const summaryReviewsPromise = isDateFiltered
        ? reviewListPromise
        : groupReviewsService.getReviews({
            limit: SUMMARY_FETCH_LIMIT,
            offset: 0,
            ...summaryDateFilters,
          });

      const [scanReportsResult, reviewsResult, summaryReviewsResult] = await Promise.allSettled([
        scanReportsService.getReports({
          scope: 'global',
          limit: SUMMARY_FETCH_LIMIT,
          orderBy: 'createdAt',
          order: 'desc',
          ...summaryDateFilters,
        }),
        reviewListPromise,
        summaryReviewsPromise,
      ]);

      const nextScanReports = scanReportsResult.status === 'fulfilled' ? (scanReportsResult.value?.reports || []) : [];
      const nextReviews = reviewsResult.status === 'fulfilled' ? (reviewsResult.value?.reviews || []) : [];
      const summaryReviews = summaryReviewsResult.status === 'fulfilled' ? (summaryReviewsResult.value?.reviews || []) : nextReviews;
      setHasMore(!isDateFiltered && Boolean(reviewsResult.status === 'fulfilled' && reviewsResult.value?.hasMore));
      setPage(isDateFiltered ? 1 : pageToLoad);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Build fallback keyword lookup by scanned group identifier.
      const fallbackKeywordByGroup = new Map();
      const fallbackLlmByGroup = new Map();
      nextScanReports.forEach((report) => {
        const keyword = getReportKeywordQuery(report);
        collectScanGroupKeys(report).forEach((groupKey) => {
          if (!fallbackKeywordByGroup.has(groupKey)) {
            if (keyword) {
              fallbackKeywordByGroup.set(groupKey, keyword);
            }
          }

          const groups = Array.isArray(report?.groupsDetails) ? report.groupsDetails : [];
          const matchedGroup = groups.find((group) => normalizeGroupIdentifier(group?.groupKey || group?.groupLink || group?.groupName || '') === groupKey);
          if (matchedGroup && !fallbackLlmByGroup.has(groupKey)) {
            fallbackLlmByGroup.set(groupKey, {
              llmLabel: matchedGroup.llmLabel || matchedGroup.llm_label || '',
              llmDecision: normalizeLlmDecision(matchedGroup.llmDecision || matchedGroup.llm_decision || matchedGroup.llmLabel || matchedGroup.llm_label),
              llmReason: matchedGroup.llmReason || matchedGroup.llm_reason || '',
              llmConfidence: matchedGroup.llmConfidence ?? matchedGroup.llm_confidence ?? null,
            });
          }
        });
      });

      const normalizedReviews = nextReviews
        .map((review) => ({
          id: review.id,
          ...review,
          llmLabel:
            review.llmLabel ||
            review.llm_label ||
            fallbackLlmByGroup.get(normalizeGroupIdentifier(review.groupLink))?.llmLabel ||
            fallbackLlmByGroup.get(normalizeGroupIdentifier(review.groupName))?.llmLabel ||
            '',
          llmDecision:
            normalizeLlmDecision(
              review.llmDecision ||
              review.llm_decision ||
              review.llmLabel ||
              review.llm_label ||
              fallbackLlmByGroup.get(normalizeGroupIdentifier(review.groupLink))?.llmDecision ||
              fallbackLlmByGroup.get(normalizeGroupIdentifier(review.groupName))?.llmDecision
            ),
          llmReason:
            review.llmReason ||
            review.llm_reason ||
            fallbackLlmByGroup.get(normalizeGroupIdentifier(review.groupLink))?.llmReason ||
            fallbackLlmByGroup.get(normalizeGroupIdentifier(review.groupName))?.llmReason ||
            '',
          llmConfidence:
            review.llmConfidence ??
            review.llm_confidence ??
            fallbackLlmByGroup.get(normalizeGroupIdentifier(review.groupLink))?.llmConfidence ??
            fallbackLlmByGroup.get(normalizeGroupIdentifier(review.groupName))?.llmConfidence ??
            null,
          keywordQuery:
            review.keywordQuery ||
            review.searchKeyword ||
            review.keyword ||
            fallbackKeywordByGroup.get(normalizeGroupIdentifier(review.groupLink)) ||
            fallbackKeywordByGroup.get(normalizeGroupIdentifier(review.groupName)) ||
            '',
          timestamp: review.timestamp ? new Date(review.timestamp) : null
        }))
        .sort((a, b) => {
          if (!a.timestamp) return 1;
          if (!b.timestamp) return -1;
          return b.timestamp.getTime() - a.timestamp.getTime();
        });

      setReviews(normalizedReviews);
      setSummary(buildDatabaseSummary(nextScanReports, summaryReviews));

      const issues = [];
      if (scanReportsResult.status === 'rejected') {
        issues.push(scanReportsResult.reason?.message || 'Failed to load scan reports');
      }
      if (reviewsResult.status === 'rejected') {
        issues.push(reviewsResult.reason?.message || 'Failed to load group reviews');
      }
      if (summaryReviewsResult.status === 'rejected') {
        issues.push(summaryReviewsResult.reason?.message || 'Failed to load database summary reviews');
      }

      if (issues.length > 0) {
        setError(issues.join('. '));
      } else {
        setError('');
        console.log(`[FrudDB] ✅ Loaded page ${pageToLoad} with ${normalizedReviews.length} group reviews and ${nextScanReports.length} scan reports`);
      }
    } catch (err) {
      console.error('[FrudDB] ❌ Failed to load database overview:', err);

      let errorMessage = 'Failed to load database overview';
      if (err.status === 401) {
        errorMessage = 'Authentication failed - please check API credentials';
      } else if (err.status === 404) {
        errorMessage = 'No database records found yet';
      } else if (err.data?.detail) {
        errorMessage = err.data.detail;
      } else if (err.message) {
        errorMessage += ': ' + err.message;
      }

      setError(errorMessage);
      setReviews([]);
      setSummary(INITIAL_SUMMARY);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Load database overview once on mount
  useEffect(() => {
    loadDatabaseOverview(1);
    // No polling - user can refresh manually if needed
  }, [loadDatabaseOverview]);

  const summaryCards = useMemo(() => ([
    {
      title: 'Total scans',
      value: summary.totalScans,
      caption: 'Saved ',
      short: 'SC',
      color: '#0f766e'
    },
    {
      title: 'Groups tracked',
      value: summary.totalScannedGroups,
      caption: '',
      short: 'GR',
      color: '#174a2c'
    },
    {
      title: 'Reviewed groups',
      value: summary.reviewedGroups,
      caption: '',
      short: 'RV',
      color: '#2563eb'
    },
    {
      title: 'Fraudulent',
      value: summary.fraudulentGroups,
      caption: '',
      short: 'FR',
      color: '#dc2626'
    },
    {
      title: 'Suspicious',
      value: summary.suspiciousGroups,
      caption: 'Monitor',
      short: 'SP',
      color: '#f59e0b'
    },
    {
      title: 'Genuine',
      value: summary.genuineGroups,
      caption: '',
      short: 'GN',
      color: '#16a34a'
    },
    {
      title: 'Unreviewed ',
      value: summary.unmarkedGroups,
      caption: '',
      short: 'UN',
      color: '#6b7280'
    }
  ]), [summary]);

  const filtered = useMemo(() => {
    if (!search.trim()) return reviews;
    const term = search.toLowerCase();
    return reviews.filter((r) =>
      (r.groupName || '').toLowerCase().includes(term) ||
      (r.groupLink || '').toLowerCase().includes(term)
    );
  }, [reviews, search]);

  const dedupedLatest = useMemo(() => {
    const latestByGroup = new Map();

    filtered.forEach((r) => {
      const linkKey = normalizeGroupIdentifier(r.groupLink);
      const nameKey = normalizeGroupIdentifier(r.groupName);
      const key = linkKey || nameKey || `id:${r.id}`;
      const existing = latestByGroup.get(key);

      if (!existing) {
        latestByGroup.set(key, r);
        return;
      }

      const existingTime = existing.timestamp ? existing.timestamp.getTime() : 0;
      const currentTime = r.timestamp ? r.timestamp.getTime() : 0;
      if (currentTime > existingTime) {
        latestByGroup.set(key, r);
      }
    });

    return Array.from(latestByGroup.values());
  }, [filtered]);

  const filterCounts = useMemo(() => {
    const counts = { all: dedupedLatest.length, genuine: 0, suspicious: 0, fraudulent: 0, blocked: 0, not_blocked: 0 };
    dedupedLatest.forEach((r) => {
      const status = normalizeReviewStatus(r);
      if (status === 'genuine') counts.genuine += 1;
      if (status === 'suspicious') counts.suspicious += 1;
      if (status === 'fraudulent') {
        counts.fraudulent += 1;
        if (r.blockedStatus === 'blocked') counts.blocked += 1;
        else counts.not_blocked += 1;
      }
    });
    return counts;
  }, [dedupedLatest]);

  const visibleReviews = useMemo(() => {
    return dedupedLatest.filter((r) => {
      const status = normalizeReviewStatus(r);
      if (activeFilter === 'all') return true;
      if (activeFilter === 'genuine' || activeFilter === 'fraudulent' || activeFilter === 'suspicious') return status === activeFilter;
      if (activeFilter === 'blocked') return status === 'fraudulent' && r.blockedStatus === 'blocked';
      if (activeFilter === 'not_blocked') return status === 'fraudulent' && r.blockedStatus !== 'blocked';
      return true;
    });
  }, [dedupedLatest, activeFilter]);

  const getReviewer = (review) => review.officerId || review.reviewedBy || review.userId || 'System';

  const getReason = (review) => review.reason || review.reviewReason || review.moderationReason || '';

  const formatTimestamp = (value) => {
    if (!value) return 'N/A';
    return value.toLocaleString();
  };

  const downloadFilteredCsv = () => {
    if (!visibleReviews.length) return;

    const headers = [
      'Group Name',
      'Group Link',
      'Review Status',
      'Blocked Status',
      'Marked By',
      'Marked On',
      'Keyword Query',
      'Reason',
      'AI Decision',
      'AI Confidence',
      'Search Engine Source',
    ];

    const rows = visibleReviews.map((review) => {
      const normalizedStatus = normalizeReviewStatus(review);
      const isBlocked = review.blockedStatus === 'blocked';
      const sources = Array.isArray(review.sEngine) ? review.sEngine.join(' | ') : '';
      return [
        review.groupName || 'Unknown Group',
        review.groupLink || '',
        normalizedStatus === 'fraudulent' ? 'Fraudulent' : normalizedStatus === 'suspicious' ? 'Suspicious' : normalizedStatus === 'genuine' ? 'Genuine' : 'Unknown',
        normalizedStatus === 'fraudulent' ? (isBlocked ? 'Blocked' : 'Not Blocked') : '',
        getReviewer(review),
        formatTimestamp(review.timestamp),
        getReviewKeyword(review),
        getReason(review) || 'Not provided',
        llmDecisionDisplay(review.llmDecision || review.llmLabel),
        review.llmConfidence ?? '',
        sources,
      ].map(csvEscape).join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.setAttribute('download', `fraud_db_${activeFilter}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const handleReviewStatusChange = async (review, nextStatus) => {
    const statusLabel = nextStatus === 'fake' ? 'fraudulent' : nextStatus === 'suspicious' ? 'suspicious' : 'genuine';
    const reason = window.prompt(`Please enter reason for marking this group as ${statusLabel}:`, '');
    if (reason === null) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setError('Reason is required before updating review status.');
      return;
    }

    const nextReview = {
      groupLink: review.groupLink || '',
      groupName: review.groupName || '',
      status: nextStatus,
      reason: trimmedReason,
      officerId: user?.email || 'admin',
      source: 'reviewed_db_status_update',
      keywordQuery: getReviewKeyword(review),
      sEngine: Array.isArray(review.sEngine) ? review.sEngine : [],
      blockedStatus: nextStatus === 'fake' ? (review.blockedStatus || 'not_blocked') : null,
      scanReportIds: Array.isArray(review.scanReportIds) ? review.scanReportIds : [],
    };
//hello
    try {
      const response = await groupReviewsService.createReview(nextReview);
      setReviews((prev) => [
        {
          ...review,
          ...nextReview,
          id: response?.reviewId || `${review.id || 'review'}_${Date.now()}`,
          timestamp: new Date(),
        },
        ...prev,
      ]);
    } catch (err) {
      setError(err?.message || 'Failed to update review status');
    }
  };

  const handleBlockedStatusChange = async (review, blockedStatus) => {
    try {
      await groupReviewsService.updateBlockedStatus({
        groupLink: review.groupLink || '',
        groupName: review.groupName || '',
        blockedStatus,
        officerId: user?.email || 'admin',
      });

      setReviews((prev) =>
        prev.map((item) =>
          item.id === review.id
            ? { ...item, blockedStatus }
            : item
        )
      );
    } catch (err) {
      setError(err?.message || 'Failed to update blocked status');
    }
  };

  return (
    <div className="page-shell fraud-db-page">
      <header className="top-bar">
        <div>
          <h1 className="page-title">Reviewed Database</h1>
          <p className="page-subtitle">All-time operational summary of scans, hits, and verdicts.</p>
        </div>
      </header>

      <div className="card fraud-db-summary-card">
        <div className="section-head">
          <div>
            <div className="eyebrow" style={{ color: '#0f766e' }}>Overview</div>
            <h3 className="section-title">Database status at a glance</h3>
          </div>
          {loading ? (
            <div className="pill">Loading summary</div>
          ) : (
            <div className="pill">Updated {summary.generatedAt ? formatTimestamp(new Date(summary.generatedAt)) : 'just now'}</div>
          )}
        </div>

        <div className="stat-grid fraud-db-summary-grid">
          {summaryCards.map((card) => (
            <div className="stat-card fraud-db-summary-stat" key={card.title}>
              <div className="stat-icon" style={{ background: card.color }}>
                {card.short}
              </div>
              <div className="stat-body">
                <div className="stat-value">{card.value}</div>
                <div className="stat-label">{card.title}</div>
                <div className="stat-label" style={{ fontSize: 12 }}>{card.caption}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card input-card fraud-db-filter-card">
        <div className="section-head">
          <div>
            <div className="eyebrow">Filter</div>
            <h3 className="section-title">Search Database</h3>
          </div>
          <button
            type="button"
            className="primary-btn fraud-db-download-btn"
            onClick={downloadFilteredCsv}
            disabled={visibleReviews.length === 0}
          >
            Download Filtered CSV
          </button>
        </div>
        <input
          className="form-input"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by group name or link..."
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 12, alignItems: 'end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: '0.02em' }}>Start Date</span>
            <input
              className="form-input"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: '0.02em' }}>End Date</span>
            <input
              className="form-input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
          <div style={{ display: 'flex', alignItems: 'end', minHeight: 100 }}>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => {
                setStartDate('');
                setEndDate('');
              }}
              disabled={!startDate && !endDate}
              style={{
                width: 'auto',
                minWidth: 120,
                height: 40,
                opacity: !startDate && !endDate ? 0.55 : 1,
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                padding: '0 16px',
                fontSize: 13,
                fontWeight: 700,
                borderRadius: 10,
              }}
            >
              Clear Dates
            </button>
          </div>
        </div>
        <div className="fraud-db-pill-row" style={{ marginTop: 12, gap: 8 }}>
          {[
            ['all', 'All', filterCounts.all],
            ['fraudulent', 'Fraudulent', filterCounts.fraudulent],
            ['suspicious', 'Suspicious', filterCounts.suspicious],
            ['genuine', 'Genuine', filterCounts.genuine],
            ['blocked', 'Blocked', filterCounts.blocked],
            ['not_blocked', 'Not Blocked', filterCounts.not_blocked],
          ].map(([value, label, count]) => (
            <button
              key={value}
              type="button"
              className={value === 'fraudulent' ? 'pill fraud-db-pill-critical' : 'pill'}
              onClick={() => setActiveFilter(value)}
              style={{
                opacity: activeFilter === value ? 1 : 0.65,
                cursor: 'pointer',
                ...(value === 'blocked'
                  ? {
                      background: activeFilter === value ? '#b91c1c' : '#fee2e2',
                      color: activeFilter === value ? '#fff' : '#991b1b'
                    }
                  : {}),
                ...(value === 'not_blocked'
                  ? {
                      background: activeFilter === value ? '#1d4ed8' : '#dbeafe',
                      color: activeFilter === value ? '#fff' : '#1e3a8a'
                    }
                  : {}),
                ...(value === 'suspicious'
                  ? {
                      background: activeFilter === value ? '#d97706' : '#fef3c7',
                      color: activeFilter === value ? '#fff' : '#92400e'
                    }
                  : {})
              }}
            >
              {label} ({count})
            </button>
          ))}
        </div>
      </div>

      <div className="card fraud-db-section-card">
        <div className="section-head">
          <div>
            <div className="eyebrow" style={{ color: '#DC2626' }}>Reviewed</div>
            <h3 className="section-title">Marked Entities (All Statuses)</h3>
          </div>
          {loading ? (
            <div className="pill">Loading</div>
          ) : (
            <div className="pill">
              {isDateFilterActive ? 'Filtered results' : `Page ${page}`} · {visibleReviews.length} unique entries
            </div>
          )}
        </div>

        {error && <div className="error-banner">{error}</div>}
        {!loading && !error && visibleReviews.length === 0 && (
          <p className="muted">No entities found for the selected filter.</p>
        )}

        <div className="list">
          {visibleReviews.map((r) => {
            const normalizedStatus = normalizeReviewStatus(r);
            const statusLabel = normalizedStatus === 'genuine'
              ? 'GENUINE'
              : normalizedStatus === 'fraudulent'
                ? 'FRAUDULENT'
                : normalizedStatus === 'suspicious'
                  ? 'SUSPICIOUS'
                : 'UNSPECIFIED';
            const statusStyle = {
              background: normalizedStatus === 'genuine'
                ? '#16A34A'
                : normalizedStatus === 'fraudulent'
                  ? '#DC2626'
                  : normalizedStatus === 'suspicious'
                    ? '#f59e0b'
                    : '#6B7280',
              color: '#fff'
            };
            const isBlocked = r.blockedStatus === 'blocked';

            return (
            <div className="list-item fraud-db-item" key={r.id} style={{ position: 'relative', paddingBottom: normalizedStatus === 'fraudulent' && !isBlocked ? 58 : undefined }}>
              <div className="fraud-db-item-head">
                <div className="list-title fraud-db-title-row">
                  <span>{r.groupName || 'Unknown Group'}</span>
                  <span className="tag fraud-db-status-tag" style={statusStyle}>{statusLabel}</span>
                </div>
                <div className="list-meta fraud-db-time">{formatTimestamp(r.timestamp)}</div>
              </div>

              {r.groupLink && (
                <div className="list-sub fraud-db-link-row">
                  <a href={r.groupLink} target="_blank" rel="noreferrer">{r.groupLink}</a>
                </div>
              )}

              <div className="fraud-db-meta-row">
                <span className="tag fraud-db-officer-tag">Marked by: {getReviewer(r)}</span>
                <span className="tag fraud-db-reason-tag">Marked on: {formatTimestamp(r.timestamp)}</span>
                {getReviewKeyword(r) && (
                  <span className="tag fraud-db-source-tag">Keyword: {getReviewKeyword(r)}</span>
                )}
                <span className="tag fraud-db-reason-tag">Reason: {getReason(r) || 'Not provided'}</span>
                {(r.sEngine && r.sEngine.length > 0) && (
                  <span className="tag fraud-db-source-tag" style={{ background: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}>
                    Source: {r.sEngine.map(eng => eng.charAt(0).toUpperCase() + eng.slice(1)).join(', ')}
                  </span>
                )}
                 {(r.llmDecision || r.llmLabel) && (
                  <span className="tag" style={{
                    background:
                      normalizeLlmDecision(r.llmDecision || r.llmLabel) === 'fraud' ? '#fef2f2' :
                      normalizeLlmDecision(r.llmDecision || r.llmLabel) === 'uncertain' ? '#fffbeb' : '#f0fdf4',
                    color:
                      normalizeLlmDecision(r.llmDecision || r.llmLabel) === 'fraud' ? '#dc2626' :
                      normalizeLlmDecision(r.llmDecision || r.llmLabel) === 'uncertain' ? '#b45309' : '#166534'
                 }}>AI: {llmDecisionDisplay(r.llmDecision || r.llmLabel)}</span>
                )}
                {normalizedStatus === 'fraudulent' && (
                  <span
                    className="tag"
                    style={{
                      background: isBlocked ? '#fee2e2' : '#dbeafe',
                      color: isBlocked ? '#991b1b' : '#1e3a8a',
                      fontWeight: 700
                    }}
                  >Status:
                    {isBlocked ? ' Blocked' : ' Not Blocked'}
                  </span>
                )}
              </div>
              {normalizedStatus === 'suspicious' && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="action-btn btn-danger"
                    onClick={() => handleReviewStatusChange(r, 'fake')}
                    style={{ minWidth: 140 }}
                  >
                    Mark Fraudulent
                  </button>
                  <button
                    type="button"
                    className="action-btn btn-success"
                    onClick={() => handleReviewStatusChange(r, 'genuine')}
                    style={{ minWidth: 140 }}
                  >
                    Mark Genuine
                  </button>
                </div>
              )}
              {normalizedStatus === 'fraudulent' && !isBlocked && (
                <button
                  type="button"
                  className="action-btn btn-danger"
                  onClick={() => handleBlockedStatusChange(r, 'blocked')}
                  style={{ position: 'absolute', right: 16, bottom: 14, minWidth: 140 }}
                >
                  Mark Blocked
                </button>
              )}
            </div>
            );
          })}
        </div>
        {!loading && !isDateFilterActive && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, marginTop: 20 }}>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => loadDatabaseOverview(Math.max(1, page - 1))}
              disabled={page === 1}
              style={{
                minWidth: 120,
                height: 42,
                padding: '0 16px',
                borderRadius: 999,
                border: page === 1 ? '1px solid #e2e8f0' : '1px solid #cbd5e1',
                background: page === 1 ? '#f8fafc' : '#ffffff',
                color: page === 1 ? '#94a3b8' : '#0f172a',
                fontWeight: 700,
                fontSize: 14,
                boxShadow: page === 1 ? 'none' : '0 8px 18px rgba(15, 23, 42, 0.06)',
              }}
            >
              ← Previous
            </button>
            <span style={{
              minWidth: 92,
              height: 42,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 16px',
              borderRadius: 999,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              fontWeight: 800,
              fontSize: 14,
              color: '#0f172a'
            }}>
              Page {page}{hasMore ? '+' : ''}
            </span>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => loadDatabaseOverview(page + 1)}
              disabled={!hasMore}
              style={{
                minWidth: 100,
                height: 42,
                padding: '0 16px',
                borderRadius: 999,
                border: !hasMore ? '1px solid #e2e8f0' : '1px solid #174a2c',
                background: !hasMore ? '#f8fafc' : '#174a2c',
                color: !hasMore ? '#94a3b8' : '#ffffff',
                fontWeight: 700,
                fontSize: 14,
                boxShadow: !hasMore ? 'none' : '0 10px 20px rgba(23, 74, 44, 0.18)',
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

